<template>
  <div class="flex w-full relative bg-gray-50 dark:bg-navy-1000">
    <ConfigurationSidebar
      @select="selectSideBarOption"
    />
    <!-- ACTIVE CONFIGURATION PAGE -->
    <div
      id="configurationScrollable"
      class="flex flex-col w-full bg-gray-50 dark:bg-navy-1000"
    >
      <!-- CONFIGURATION HEADER -->
      <div class="flex-col items-center flex">
        <div
          class="sectionHeader flex items-center justify-center w-full z-20 lg:z-15 lg:transition-all duration-200 transform"
          :class="headerScrolled"
        >
          <!-- CONFIGURATION LOGO -->
          <ConfigurationLogo class="transition-all duration-200" :class="logoClass" />
        </div>
      </div>
      <!-- SELECTED CONFIGURATION COMPONENT -->
      <div v-if="readyToLoad" :class="pageClass">
        <component v-if="currentConfigComponent" :is="currentConfigComponent" />
        <section v-else class="py-16 px-5 flex flex-col justify-center">
          <div class="flex justify-center my-8">
            <div class="gear-container flex">
              <div class="gears-counter w-20 h-16">
                <font-awesome-icon icon="fa-solid fa-gear-complex" class="w-16 h-16 text-primary dark:text-white gear-rotate" />
              </div>
              <div class="gears-left-counter w-20 h-16 secondary-gear">
                <font-awesome-icon icon="fa-solid fa-gear-complex" class="w-16 h-16 dark:text-primary-100 gear-rotate-left text-shadow" />
              </div>
            </div>
          </div>
          <h2>
            To fine-tune your configurations, please choose an option from the sidebar.
          </h2>
        </section>
      </div>
      <div v-else class="w-full h-full">
        <LoadingSpinnerPage />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex"
import { getPermissionSets } from "@/services/permissions.services.js"

import { defineAsyncComponent } from "vue"
// Lazy-loaded configuration views
const UsersView = defineAsyncComponent({
  loader: () => import("./Users/UsersView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const UsersLogTable = defineAsyncComponent({
  loader: () => import("./Users/UsersLogTable.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const AssetCategoryView = defineAsyncComponent({
  loader: () => import("./AssetCategoryView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const AssetModelsView = defineAsyncComponent({
  loader: () => import("./AssetModelsView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const InventoryCategoryView = defineAsyncComponent({
  loader: () => import("./InventoryCategoryView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const ChemicalThresholdsView = defineAsyncComponent({
  loader: () => import("./Thresholds/ChemicalThresholdsView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const ChemicalGroupView = defineAsyncComponent({
  loader: () => import("./Chemical/ChemicalGroupView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const LocationView = defineAsyncComponent({
  loader: () => import("./LocationView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const ManufacturerView = defineAsyncComponent({
  loader: () => import("./ManufacturerView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const VendorPage = defineAsyncComponent({
  loader: () => import("./Vendors/VendorPage.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const UnitOfMeasureView = defineAsyncComponent({
  loader: () => import("./UnitOfMeasure/UnitOfMeasureView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const RegionsView = defineAsyncComponent({
  loader: () => import("./RegionsView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const PermissionsLog = defineAsyncComponent({
  loader: () => import("./Permissions/PermissionsLog.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const PermissionsView = defineAsyncComponent({
  loader: () => import("./Permissions/PermissionsView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
import ConfigurationSidebar from "../components/ConfigurationSidebar.vue"
import LoadingSpinnerPage from "@/components/Loading/LoadingSpinner/LoadingSpinnerPage.vue"
import ConfigurationLogo from "@/assets/Configuration_Navigation_Icon_Full_Color.svg"
import { getAllEmployees } from "../../Site-Track/services/ManagerService"
import { getImagesBySources } from "@/services/images.service"


export default {
  name: "ConfigurationPage",
  components: {
    ConfigurationSidebar,
    LoadingSpinnerPage,
    ConfigurationLogo
  },
  computed: {
    ...mapState([
      "headers",
      "screen",
      "orientation"
    ]),
    ...mapGetters([
      "showingModuleHeader",
      "atTopOfPage",
      "loadedGlobalValues",
      "getIsTenantSuperuser",
      "getUserManagementAccess",
      "getEmployeesUserId"
    ]),
    currentConfigComponent() {
      const path = this.$route.path

      if (path.includes("Users")) return UsersView
      if (path.includes("user_log")) return UsersLogTable
      if (path.includes("Asset/models")) return AssetModelsView
      if (path.includes("Asset/categories")) return AssetCategoryView
      if (path.includes("Inventory/categories")) return InventoryCategoryView
      if (path.includes("Thresholds/chemical")) return ChemicalThresholdsView
      if (path.includes("Chemical_Groups")) return ChemicalGroupView
      if (path.includes("Locations")) return LocationView
      if (path.includes("Manufacturers")) return ManufacturerView
      if (path.includes("Vendors")) return VendorPage
      if (path.includes("Tenant/UnitOfMeasure")) return UnitOfMeasureView
      if (path.includes("Tenant/Regions")) return RegionsView
      if (path.includes("Permissions/logs")) return PermissionsLog
      if (path.includes("Permissions")) return PermissionsView

      // default to empty instruction section
      return null
    },
    headerScrolled() {
      if (this.isMobileLandscape && !this.isTablet && !this.screen.xl) return "hidden"
      let className = "md:fixed lg:shadow-md bg-gray-50 dark:bg-navy-1000 lg:bg-white-off "
      if (this.atTopOfPage || !this.screen.lg) {
        className += "h-16 lg:h-24 "
      }
      else className += "shadow-lg h-24 lg:h-16 "
      if (!this.showingModuleHeader && this.screen.lg) className += "-translate-y-full md:translate-y-0 "
      else className += "-translate-y-0 "
      return className
    },
    pageClass() {
      let classes = "flex flex-col relative "
      if (!this.isMobileLandscape || (this.isTablet)) classes += "md:mt-16 lg:mt-24 "
      return classes
    },
    path() {
      return this.$route.path
    },
    logoClass() {
      if (this.atTopOfPage && this.screen.lg) return "h-14 lg:h-20 pt-3 pb-2 my-0"
      else return "h-14 lg:h-16 py-2" 
    },
    needLoad() {
      // MARK IF PATH REQUIRES STATE VALUES PRELOAD
      return {
        "/Configuration/Users": [
          "permissionSets",
        ],
        "/Configuration/user_log": [
          "allEmployeesData",
        ],
        "/Configuration/Permissions/logs": [
          "allEmployeesData",
        ],
      }
    },
    readyToLoad() {
      if (!this.needLoad[this.path]) return true
      for (let needToLoad of this.needLoad[this.path]) {
        if (!this.loadedGlobalValues[needToLoad]) return false
      }
      return true
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    isMobileLandscape() {
      return this.isMobile && this.orientation.landscape
    },
    isTablet() {
      return /ipad|tablet/i.test(window.navigator.userAgent.toLowerCase())
    },
  },
  watch: {
    path(val) {
      this.setSTPage(val)
    }
  },
  mounted() {
    if (this.getUserManagementAccess || this.getIsTenantSuperuser) {
      this.loadPermissionSets()
      this.loadEmployees()
    }
    this.showModuleHeader(true)
  },
  methods: {
    ...mapActions(["setFullEmployeeData"]),
    ...mapMutations([
      "setSTPage",
      "showModuleHeader",
      "setPrompt",
      "setAllPermissionSets",
      "setAllEmployeeAvatars"
    ]),
    selectSideBarOption(option) {
      this.setSTPage(option)
    },
    async loadPermissionSets() {
      await getPermissionSets(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setAllPermissionSets(response.data.results)
        }
        else {
          console.log(response)
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions to access permission sets.", "closePrompt", null, { header: "Error" }])
          }
          else this.setPrompt(["Unable to load the user's permission sets.", "closePrompt", null, { header: "Error" }])
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Unable to load the user's permission sets.", "closePrompt", null, { header: "Error" }])
      })
    },
    loadEmployees() {
      let employeeAccess = this.getIsTenantSuperuser
        ||
        // will need to replace with new employee endpoint not just for managers
        this.getUserPermissions.site_track?.manager_tools?.employee_management?.main_view?.object?.read_bool
      if (!employeeAccess) {
        this.setFullEmployeeData([])
        return
      }
      getAllEmployees(this.headers)
      .then(response => {
        if (response?.status === 200) {
          this.setFullEmployeeData(response.data.results)
          this.loadAllEmployeeAvatars(response.data.results)
        }
        else {
          console.log(response)
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions to access employee data.", "closePrompt", null, { header: "Error" }])
          }
        }
      }, (error) => {
        console.log(error)
      })
    },
    loadAllEmployeeAvatars(employees) {
      if (Object.keys(this.getEmployeesUserId).length === 0) return
      let userIds = []
      for (let user of employees) {
        if (user.user_id) userIds.push(user.user_id)
      }
      getImagesBySources(userIds, this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setAllEmployeeAvatars(response.data.results)
        }
        else console.log(response)
      }).catch(error => console.log(error))
    },
  },
};
</script>

<style scoped>
.secondary-gear {
  /* transition: all 0.3s ease; */
  margin-left: -1.52rem;
  margin-top: 1.176rem;

}
.gear-rotate, .gears-counter {
  -webkit-animation-name: gear-rotate;
  -webkit-animation-iteration-count: infinite;
  -webkit-animation-timing-function: linear;
  animation-name: gear-rotate;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}
.gear-rotate-left, .gears-left-counter {
  -webkit-animation-name: gear-rotate-left;
  -webkit-animation-iteration-count: infinite;
  -webkit-animation-timing-function: linear;
  animation-name: gear-rotate-left;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}
.gears-counter, .gears-left-counter {
  -webkit-animation-duration: 1s;
  -webkit-animation-play-state: paused;
  animation-duration: 1s;
  animation-play-state: paused;
}
.gear-container:hover .gears-counter, .gear-container:hover .gears-left-counter {
  -webkit-animation-play-state: running;
  animation-play-state: running;
}
.gear-rotate, .gear-rotate-left {
  -webkit-animation-duration: 5s;
  animation-duration: 5s;
}
@-webkit-keyframes gear-rotate {
  0% { 
    -webkit-transform: rotate(0deg);
  }
  100% { 
    -webkit-transform: rotate(-180deg); 
  }
}

@-moz-keyframes gear-rotate {
  0% { 
    transform: rotate(0deg);
  }
  100% { 
    transform: rotate(-180deg); 
  }
}

@keyframes gear-rotate {
  0% { 
    transform: rotate(0deg); 
  }
  100% { 
    transform: rotate(-180deg); 
  }
}

@-webkit-keyframes gear-rotate-left {
 0% {
   -webkit-transform: rotate(30deg); 
  }
  100% {
    -webkit-transform: rotate(210deg);
  }
}

@-moz-keyframes gear-rotate-left {
 0% { 
   -webkit-transform: rotate(30deg); 
  }
  100% { 
    -webkit-transform: rotate(210deg);
  }
}

@keyframes gear-rotate-left {
 0% { 
   -webkit-transform: rotate(30deg); 
  }
  100% { 
    -webkit-transform: rotate(210deg);
  }
}
</style>